/**
 * Historic Encounters 1–4, as `HISTORIC.DAT` orders them.
 *
 * Every `title`, `briefing` line and seat `lines` entry below is **verbatim**
 * from the file — including its double spaces after full stops, which is how it
 * stores them — and `tools/bake`'s `historic-encounters.test.ts` asserts that
 * against the real bytes so this table can never drift from the data. The
 * `aceIndex` and `aircraftCode` fields are the file's own bytes.
 *
 * Everything else — where the aeroplanes are, how many there are, the region,
 * the era, the weather, the date — is this port's, because the file carries
 * none of it (`historic-scene.ts`). The sentence each layout comes from is
 * quoted at the layout.
 */

import { BALLOON_ALTITUDE_M, wingman, type HistoricEncounterData, type HistoricFlight } from "./historic-scene.ts";

/** The Sopwith Camel flight patrolling over Roth's balloons. */
const CAMEL_LEADER: HistoricFlight = {
  id: "camel-1",
  nation: "allied",
  aircraft: "CAMEL",
  callsign: "Camel leader",
  skill: "veteran",
  seat: "allied",
  x: 220,
  altitudeM: 1650,
  z: -4200,
  headingRad: Math.PI,
};

/** McCudden, with the two D.H.2s that pounced on Voss behind and below him. */
const MCCUDDEN: HistoricFlight = {
  id: "mccudden",
  nation: "allied",
  aircraft: "AIRCO",
  callsign: "James McCudden",
  skill: "ace",
  aceId: "mccudden",
  seat: "allied",
  x: 280,
  altitudeM: 1500,
  z: 1350,
  headingRad: Math.PI,
};

/**
 * **1. Hawker Meets His Match** — 23 November 1916, over Bapaume. The fight
 * that made Richthofen. Both seats say the same thing about the geometry and
 * about who else is left: *"The other planes have left the area."* So this is
 * the file's only true one-on-one, and Hawker starts higher.
 */
const HAWKER: HistoricEncounterData = {
  id: "historic-1",
  title: "Hawker Meets His Match",
  when: "23 November 1916",
  era: "mid",
  region: "somme",
  weather: { sky: "cloudy", wind: "light", timeOfDay: "daytime" },
  briefing: [
    "While on patrol, Major Lanoe Hawker and one other British pilot discovered",
    "five Albatros D.IIs.  Hawker and his companion each picked a target and engaged",
    "the enemy.  Hawker chose the plane of Manfred von Richthofen.  The other",
    "German pilots attacked Hawker's companion, quickly chasing him toward",
    "the front.  Hawker and Richthofen fought a wild turning melee.",
  ],
  flights: [
    {
      id: "richthofen",
      nation: "german",
      aircraft: "ALBA_D2",
      callsign: "Manfred von Richthofen",
      skill: "ace",
      aceId: "richthofen",
      seat: "german",
      x: 0,
      altitudeM: 1050,
      z: 3300,
      headingRad: 0,
    },
    {
      id: "hawker",
      nation: "allied",
      aircraft: "AIRCO",
      callsign: "Lanoe Hawker",
      skill: "ace",
      aceId: "hawker",
      seat: "allied",
      x: 160,
      altitudeM: 1500,
      z: 2000,
      headingRad: Math.PI,
    },
  ],
  german: {
    aceIndex: 0,
    aircraftCode: 19,
    playable: true,
    lines: ["You are Richthofen.  You will start with Hawker above you.", "The other planes have left the area."],
    objective: { kind: "destroyAircraft", label: "Shoot down Hawker", count: 1 },
  },
  allied: {
    aceIndex: 15,
    aircraftCode: 1,
    playable: true,
    lines: ["You are Hawker.  Richthofen will start below you.", "The other planes have already left the area."],
    objective: { kind: "destroyAircraft", label: "Shoot down Richthofen", count: 1 },
  },
  portNotes: [
    "The four other Albatros D.IIs and Hawker's companion are in the briefing but not in the air: both seats say the rest of the fight has already left, and this port takes the file at its word rather than inventing six aeroplanes it does not describe.",
  ],
};

/**
 * **2. Lt. Roth Goes Balloon Hunting** — Jasta 16 over the Ypres salient. The
 * only encounter where the file names the aeroplane in the briefing prose
 * (*"his Albatros D.Va"*), which is how the aircraft-code byte was first read.
 * Roth is not one of the 25 aces, so the file writes an unnamed-pilot code.
 */
const ROTH: HistoricEncounterData = {
  id: "historic-2",
  title: "Lt. Roth Goes Balloon Hunting",
  when: "Autumn 1917",
  era: "mid",
  region: "dunkirk",
  weather: { sky: "partly cloudy", wind: "light", timeOfDay: "daytime" },
  briefing: [
    "Lt. Roth of Jasta 16 took to the sky in his Albatros D.Va to do some",
    "routine balloon hunting.  Starting behind and to the north of Ypres,",
    "Roth discovered six British balloons.",
  ],
  balloonNation: "allied",
  balloons: [
    { id: "balloon-1", nation: "allied", label: "Drachen 1", x: -650, altitudeM: BALLOON_ALTITUDE_M, z: -3350 },
    { id: "balloon-2", nation: "allied", label: "Drachen 2", x: 40, altitudeM: BALLOON_ALTITUDE_M, z: -3500 },
    { id: "balloon-3", nation: "allied", label: "Drachen 3", x: 700, altitudeM: BALLOON_ALTITUDE_M, z: -3300 },
  ],
  flights: [
    {
      id: "roth",
      nation: "german",
      aircraft: "ALBA_D5",
      callsign: "Lt. Roth",
      skill: "ace",
      seat: "german",
      x: 0,
      altitudeM: 1500,
      z: -2400,
      headingRad: 0,
    },
    CAMEL_LEADER,
    wingman(CAMEL_LEADER, { id: "camel-2", callsign: "Camel 2", skill: "regular", dx: 200, dy: -30, dz: -110 }),
    wingman(CAMEL_LEADER, { id: "camel-3", callsign: "Camel 3", skill: "regular", dx: -200, dy: 40, dz: -110 }),
  ],
  german: {
    aceIndex: undefined,
    unnamedPilot: 101,
    aircraftCode: 23,
    playable: true,
    lines: [
      "You are Roth.  You will start near three British balloons.  After",
      "destroying them, continue South along the front in search of others.",
    ],
    objective: { kind: "burnBalloons", label: "Burn the three British balloons" },
  },
  allied: {
    aceIndex: undefined,
    unnamedPilot: 100,
    aircraftCode: 7,
    playable: true,
    lines: [
      "You are in command of a squadron of Sopwith Camels patrolling",
      "the area.  You will start within sight of Roth's plane.",
    ],
    objective: { kind: "guardBalloons", label: "Shoot Roth down before he burns the balloons", count: 1 },
  },
  portNotes: [
    "Three of the six balloons are on the map. The German seat's own line is *\"start near three British balloons … continue South along the front in search of others\"*, and this port has no rolling map to continue South along, so it flies the three the encounter starts you on.",
  ],
};

/**
 * **3. Voss Meets McCudden** — the file's own dating, not history's. The
 * briefing puts McCudden in D.H.2s and the aircraft-code bytes agree (`1` for
 * both British seats, `19` for Voss's Albatros D.II), which places it in late
 * 1916, not the September 1917 fight the two names usually mean.
 */
const VOSS: HistoricEncounterData = {
  id: "historic-3",
  title: "Voss Meets McCudden",
  when: "Winter 1916",
  era: "mid",
  region: "somme",
  weather: { sky: "overcast", wind: "still", timeOfDay: "daytime" },
  briefing: [
    "After chasing a German observation aircraft, McCudden's flight",
    "of D.H.2s noticed some flak bursts to their south.  Upon further",
    "investigation, they discovered a lone German fighter was the",
    "object of the ground fire.  Voss was the pilot.  The D.H.2s pounced",
    "on Voss while McCudden stayed east of the flight to make sure Voss",
    "could not escape.",
  ],
  flights: [
    {
      id: "voss",
      nation: "german",
      aircraft: "ALBA_D2",
      callsign: "Werner Voss",
      skill: "ace",
      aceId: "voss",
      seat: "german",
      x: 0,
      altitudeM: 900,
      z: 1900,
      headingRad: Math.PI,
    },
    MCCUDDEN,
    wingman(MCCUDDEN, { id: "dh2-2", callsign: "D.H.2 (2)", skill: "veteran", dx: -700, dy: -170, dz: -370 }),
    wingman(MCCUDDEN, { id: "dh2-3", callsign: "D.H.2 (3)", skill: "regular", dx: 340, dy: -120, dz: -410 }),
  ],
  german: {
    aceIndex: 2,
    aircraftCode: 19,
    playable: true,
    lines: [
      "You are Voss.  You have just escaped the D.H.2s and are below",
      "McCudden, flying toward the German lines.",
    ],
    objective: { kind: "destroyAircraft", label: "Fight your way clear: down all three D.H.2s", count: 3 },
  },
  allied: {
    aceIndex: 13,
    aircraftCode: 1,
    playable: true,
    lines: [
      "You are McCudden.  Voss has just escaped from your D.H.2s and is",
      "flying below you toward the German lines.",
    ],
    objective: { kind: "destroyAircraft", label: "Shoot Voss down before he reaches the German lines", count: 1 },
  },
  portNotes: [
    "*\"McCudden stayed east of the flight\"* is in the briefing and in this layout — he is off to one side and above, with the other two D.H.2s behind and lower.",
    "Voss is flying for his own lines, but nothing here ends the mission when he crosses them: the encounter is decided by the guns, on both seats.",
  ],
};

/**
 * **4. The Master Meets His End** — 21 April 1918, the Morlancourt Ridge. A
 * three-aeroplane chain at nought feet: Richthofen on May's Camel, Brown on
 * Richthofen. Both seats describe the same instant from opposite ends of it.
 */
const MASTER: HistoricEncounterData = {
  id: "historic-4",
  title: "The Master Meets His End",
  when: "21 April 1918",
  era: "late",
  region: "somme",
  weather: { sky: "partly cloudy", wind: "strong", timeOfDay: "daytime" },
  briefing: [
    "Richthofen's squadron engaged two R.E.8s who were on a photo-",
    "recon mission.  They were distracted from their prey by the arrival of",
    "209 Squadron, lead by Roy Brown.  During the ensuing melee a rookie",
    "British pilot broke from the fray and Richthofen followed.  Brown also",
    "disengaged from the main battle in order to rescue the other British",
    "pilot.",
  ],
  flights: [
    {
      id: "rookie",
      nation: "allied",
      aircraft: "CAMEL",
      callsign: "Wilfrid May",
      skill: "novice",
      x: 0,
      altitudeM: 520,
      z: -3000,
      headingRad: 0,
    },
    {
      id: "richthofen",
      nation: "german",
      aircraft: "FOKDR1",
      callsign: "Manfred von Richthofen",
      skill: "ace",
      aceId: "richthofen",
      seat: "german",
      x: 30,
      altitudeM: 600,
      z: -2700,
      headingRad: 0,
    },
    {
      id: "brown",
      nation: "allied",
      aircraft: "CAMEL",
      callsign: "Roy Brown",
      skill: "ace",
      seat: "allied",
      x: -160,
      altitudeM: 860,
      z: -2350,
      headingRad: 0,
    },
  ],
  german: {
    aceIndex: 0,
    aircraftCode: 21,
    playable: true,
    lines: [
      "You are Richthofen.  You start as your plane overtakes the fleeing",
      "British pilot's Sopwith Camel.",
    ],
    objective: { kind: "destroyAircraft", label: "Take the Camel — and the one behind you", count: 2 },
  },
  allied: {
    aceIndex: undefined,
    unnamedPilot: 101,
    aircraftCode: 7,
    playable: true,
    lines: [
      "You are Brown.  You start as you close on Richthofen.  He in turn",
      "is closing on the other British pilot.",
    ],
    objective: { kind: "destroyAircraft", label: "Shoot Richthofen off May's tail", count: 1 },
  },
  portNotes: [
    "Nought feet is the whole point of this one: all three aeroplanes start under 700 m, and the ground is as dangerous as the guns.",
    "The two R.E.8s and the rest of the melee are behind you and out of it, exactly as the briefing has it — the encounter is the chase, not the fight it broke out of.",
    "Roy Brown is not one of `ACE.DAT`'s 25, so the file writes an unnamed-pilot code for his seat and this port flies him at the `ace` tier on the strength of the briefing naming him.",
  ],
};

export const HISTORIC_SCENES_A: readonly HistoricEncounterData[] = [HAWKER, ROTH, VOSS, MASTER];
